import {
  ASSET_DETAIL_QUESTIONS,
  GATE_CARD_CLARIFIER_CODES,
  TOPIC_GATE_CLARIFIER_CODE,
  QUESTIONNAIRE_NOTHING_CHANGED_REPLY,
} from "@digital-twins/shared-types";
import type {
  ChatMessageOption,
  QuestionnaireChatMessage,
  QuestionnaireClarification,
} from "@digital-twins/shared-types";
import type { TFunction } from "i18next";

/**
 * Builder cards in the chat's language. Nest asks in English (its copy is
 * model-facing), and an opening-chain card's clarification record names the
 * step that asked it (`origin: 'chain'`, `code`). The builder words that card
 * from its code, `questionnaire.opening.<code>.question`, and each option from
 * its stable id, `questionnaire.opening.<code>.options.<id>`. A card no chain
 * step asked (a model's or a gate's question, PLAN's question for the study's
 * material with its upload card, a chat stored before the records) keeps its
 * own words.
 *
 * Cache scope: one-shot display copy; not an LLM prompt.
 */

/**
 * The step that asked each card, by the card's message id: an opening-chain
 * step, or one of the gates the builder words the same way (the topic gate and
 * the landing-page pricing question).
 */
export function chainCodesByMessage(
  clarifications: ReadonlyArray<QuestionnaireClarification>,
): Map<string, string> {
  const codes = new Map<string, string>();
  for (const record of clarifications) {
    const worded =
      record.origin === "chain" ||
      (record.origin === "gate" &&
        GATE_CARD_CLARIFIER_CODES.includes(record.code ?? ""));
    if (worded && record.code && record.messageId) {
      codes.set(record.messageId, record.code);
    }
  }
  return codes;
}

/**
 * The fixed lines no chain step asks: a rejected chat turn's own line (F16)
 * and the generic greeting a goal-less session opens with. A committed change
 * or first Build has none: its receipts stand when no reply is written (D2).
 */
const FIXED_LINE_KEYS: Readonly<Record<string, string>> = {
  [QUESTIONNAIRE_NOTHING_CHANGED_REPLY]: "questionnaire.notDone.nothingChanged",
  "What are you interested in learning from this study?":
    "questionnaire.opening.defaultGreeting",
  // The asset-detail question, whose English Nest stores for the models.
  ...Object.fromEntries(
    Object.entries(ASSET_DETAIL_QUESTIONS).map(([id, english]) => [
      english,
      `questionnaire.opening.asset_details.${id}`,
    ]),
  ),
};

type BuilderTranslate = (key: string, options?: Record<string, unknown>) => string;

/** A card's question; `chainCode` is the step its record names, if any. */
export function builderCardText(
  message: Pick<QuestionnaireChatMessage, "content"> & Partial<Pick<QuestionnaireChatMessage, "answerOnly">>,
  chainCode: string | null | undefined,
  translate: TFunction<"app">,
): string {
  // The keys are built from the step's code, not the typed key union.
  const t = translate as unknown as BuilderTranslate;
  // The topic gate asks the planner's own reason, then the builder's closing
  // question.
  if (chainCode === TOPIC_GATE_CLARIFIER_CODE) {
    return `${message.content} ${t(`questionnaire.opening.${chainCode}.suffix`)}`;
  }
  if (chainCode) {
    return t(`questionnaire.opening.${chainCode}.question`);
  }
  // A turn that only asked, with no answer written: said in the chat's language.
  if (message.answerOnly && !message.content.trim()) return t("questionnaire.notDone.noAnswer");
  const fixedKey = FIXED_LINE_KEYS[message.content];
  return fixedKey ? t(fixedKey) : message.content;
}

/**
 * An option's position mark, by its index on the card: A to Z, then AA, AB and
 * on. One scheme however many options a card offers (an attach card lists one
 * per item); the composer, a locked card and the free-text row all read it.
 */
export function builderOptionMark(index: number): string {
  let mark = "";
  for (let rest = index + 1; rest > 0; rest = Math.floor((rest - 1) / 26)) {
    mark = String.fromCharCode(65 + ((rest - 1) % 26)) + mark;
  }
  return mark;
}

/** An option's label; a chain card's option is worded from its id. */
export function builderOptionLabel(
  option: Pick<ChatMessageOption, "id" | "label">,
  chainCode: string | null | undefined,
  translate: TFunction<"app">,
): string {
  const t = translate as unknown as BuilderTranslate;
  return chainCode && option.id
    ? t(`questionnaire.opening.${chainCode}.options.${option.id}`)
    : option.label;
}
